import type {
    RuntimeTabularCellRenderOptions,
    RuntimeTabularColumnDescriptor,
    RuntimeTabularColumnMetadata,
} from './types';
import type {
    TableCellObjectSettings,
    TableLayoutShape,
    TableLayoutRowSettings,
    TableRowSettings,
} from '../../elements/table/contracts';
import { convertDistanceAndFloat, fixFloat, getDataType } from '../../../../../graph/data-types';
import { format } from '../../../../../utils/formatting';
import { isRecord } from '../../../../../utils/typeGuards';
import { applyNamingConvention } from '../../../../../utils/naming-conventions';

function getColumnIndexMap(columns: readonly RuntimeTabularColumnMetadata[]): Map<string, number> {
    return new Map(columns.map((column, index) => [column.key, index]));
}

export function getColumnIndexesForKeysOrThrow(
    columns: readonly RuntimeTabularColumnMetadata[],
    activeColumnKeys: readonly string[]
): number[] {
    const indexByKey = getColumnIndexMap(columns);
    return activeColumnKeys.map((columnKey) => {
        const columnIndex = indexByKey.get(columnKey);
        if (typeof columnIndex !== 'number') {
            throw new Error(`Unknown active tabular column "${columnKey}".`);
        }

        return columnIndex;
    });
}

function requireFiniteNumber(
    value: number,
    column: RuntimeTabularColumnMetadata,
    what: string
): number {
    if (!Number.isFinite(value)) {
        throw new Error(`Expected a finite ${what} for tabular column "${column.key}".`);
    }
    return value;
}

/**
 * Resolve a column's header. Measurement columns derive it from their data type so the name
 * follows the active guideline's naming conventions and the unit follows the resolved unit;
 * every other kind carries a static label.
 */
export function resolveColumnLabel<TEntry>(
    column: RuntimeTabularColumnDescriptor<TEntry>,
    options: RuntimeTabularCellRenderOptions
): string {
    if (column.kind !== 'measurement') {
        return column.label;
    }

    const name = applyNamingConvention(
        column.label ?? getDataType(column.dataType).title,
        options.namingConventions
    );

    // `applyNamingConvention` iterates the conventions rather than the tokens, so an unmapped
    // key leaves a literal `~~key~~` in the output. Rendering that into a report is worse than
    // failing, so it fails.
    if (name.includes('~~')) {
        throw new Error(
            `Unresolved naming convention token in header for column "${column.key}": ${name}`
        );
    }
    const qualified = column.qualifier ? `${name} ${column.qualifier}` : name;

    // An explicit `null` means unitless; a missing key means the unit was never resolved, which
    // would silently drop it from the header.
    if (!(column.dataType in options.units)) {
        throw new Error(
            `Missing resolved unit for measurement column "${column.key}" (${column.dataType}).`
        );
    }
    const unit = options.units[column.dataType];
    return unit ? `${qualified} (${unit})` : qualified;
}

export function formatTimeRange(startTime: number, endTime: number, timezone: string): string {
    const start = format(new Date(startTime), { timezone, hideSeconds: true });
    const end = format(new Date(endTime), { timezone, hideSeconds: true });
    return `${start} - ${end}`;
}

function requireTimezone(options: RuntimeTabularCellRenderOptions, columnLabel: string): string {
    const { timezone } = options;
    if (!timezone) {
        throw new Error(`Missing timezone for ${columnLabel}.`);
    }
    return timezone;
}

function formatTableCell(
    value: unknown,
    column: RuntimeTabularColumnMetadata,
    options: RuntimeTabularCellRenderOptions
): string {
    if (value === null || typeof value === 'undefined') {
        return '';
    }

    if (column.kind === 'timestamp') {
        if (typeof value !== 'number') {
            throw new Error(`Expected numeric timestamp for tabular column "${column.key}".`);
        }

        return format(new Date(requireFiniteNumber(value, column, 'timestamp')), {
            timezone: requireTimezone(options, `timestamp column "${column.key}"`),
        });
    }

    if (column.kind === 'timeRange') {
        if (!isRecord(value) || typeof value.from !== 'number' || typeof value.to !== 'number') {
            throw new Error(
                `Expected a { from, to } timestamp pair for time range column "${column.key}".`
            );
        }

        return formatTimeRange(
            requireFiniteNumber(value.from, column, 'range start'),
            requireFiniteNumber(value.to, column, 'range end'),
            requireTimezone(options, `time range column "${column.key}"`)
        );
    }

    if (column.kind === 'measurement') {
        if (typeof value !== 'number') {
            throw new Error(
                `Expected a numeric value for measurement column "${column.key}" (${column.dataType}).`
            );
        }

        const numeric = requireFiniteNumber(value, column, 'measurement value');

        // Convert before rounding: `amountAfterDot` describes the converted magnitude. A
        // pre-converted value is already in the profile's unit, so it only rounds.
        return String(
            column.preConverted
                ? fixFloat(column.dataType, numeric)
                : convertDistanceAndFloat(column.dataType, numeric, options.profile)
        );
    }

    if (column.kind === 'count') {
        if (typeof value !== 'number') {
            throw new Error(`Expected a numeric count for tabular column "${column.key}".`);
        }

        return String(requireFiniteNumber(value, column, 'count'));
    }

    if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        typeof value === 'bigint'
    ) {
        return String(value);
    }

    throw new Error(`Expected a scalar value for tabular column "${column.key}".`);
}

export function buildRowsFromColumns<TEntry>(
    entries: readonly TEntry[],
    columns: readonly RuntimeTabularColumnDescriptor<TEntry>[],
    activeColumnKeys: readonly string[] | undefined,
    options: RuntimeTabularCellRenderOptions
): TableRowSettings[] {
    const activeColumns = activeColumnKeys
        ? activeColumnKeys.map((columnKey) => {
              const column = columns.find((entry) => entry.key === columnKey);
              if (!column) {
                  throw new Error(`Unknown active tabular column "${columnKey}".`);
              }

              return column;
          })
        : columns;

    return entries.map((entry) => ({
        cells: activeColumns.map((column) => {
            const value =
                column.kind === 'text' && column.requiresTimezone
                    ? column.resolveCell(entry, {
                          ...options,
                          timezone: requireTimezone(options, `text column "${column.key}"`),
                      })
                    : column.resolveCell(entry, options);
            return formatTableCell(value, column, options);
        }),
    }));
}

function createIndexedCell(value: string, columnIndex: number): TableCellObjectSettings {
    return { value, columnIndex };
}

/**
 * Rows for entries that belong to one parent record, with the parent's column rendered once and
 * spanning them.
 *
 * Grouping is the caller's, by record, rather than derived from a cell: two records that happen
 * to render the same value in the spanned column are still two records, and merging them would
 * present them as one. The spanned column is named by key, not position, so reordering the
 * columns cannot silently move the span.
 */
export function buildGroupedRowsFromColumns<TEntry>(
    groups: readonly (readonly TEntry[])[],
    columns: readonly RuntimeTabularColumnDescriptor<TEntry>[],
    spanColumnKey: string,
    options: RuntimeTabularCellRenderOptions
): TableLayoutRowSettings[] {
    const [spanColumnIndex] = getColumnIndexesForKeysOrThrow(columns, [spanColumnKey]);

    return groups.flatMap((entries, groupIndex) => {
        if (entries.length === 0) {
            throw new Error(`Expected row group ${groupIndex} to contain at least one entry.`);
        }

        const rows = buildRowsFromColumns(entries, columns, undefined, options);

        return rows.map((row, rowIndex) => {
            if (rowIndex > 0) {
                return {
                    cells: row.cells
                        .filter((_cell, cellIndex) => cellIndex !== spanColumnIndex)
                        .map((cell, cellIndex) =>
                            createIndexedCell(
                                cell,
                                cellIndex < spanColumnIndex ? cellIndex : cellIndex + 1
                            )
                        ),
                };
            }

            return {
                cells: row.cells.map((cell, cellIndex) =>
                    cellIndex === spanColumnIndex && rows.length > 1
                        ? { value: cell, columnIndex: cellIndex, rowSpan: rows.length }
                        : createIndexedCell(cell, cellIndex)
                ),
            };
        });
    });
}

function buildStaticTableShape(
    settings: TableLayoutShape,
    rows: TableRowSettings[]
): TableLayoutShape {
    return {
        header: settings.header,
        rows,
        columnAlignments: settings.columnAlignments,
        columnWidths: settings.columnWidths,
    };
}

export function buildStaticTableShapeFromColumns<TEntry>(
    settings: TableLayoutShape,
    linkValue: unknown,
    columns: readonly RuntimeTabularColumnDescriptor<TEntry>[],
    parseEntries: (value: unknown) => readonly TEntry[],
    options: RuntimeTabularCellRenderOptions
): TableLayoutShape {
    return buildStaticTableShape(
        settings,
        buildRowsFromColumns(parseEntries(linkValue), columns, undefined, options)
    );
}

/**
 * The conventional leading "Date/Time" range column for an entry that carries its window as a
 * flat `{ startTime, endTime }` pair. A service whose range sits in another shape declares its
 * own `timeRange` column instead.
 */
export function createTimeRangeColumn<
    TEntry extends { startTime: number; endTime: number }
>(): RuntimeTabularColumnDescriptor<TEntry> {
    return {
        kind: 'timeRange',
        key: 'period',
        label: gettext('Date/Time'),
        defaultAlignment: 'left',
        resolveCell: (entry) => ({ from: entry.startTime, to: entry.endTime }),
    };
}

export function pickTableSettingsByColumnIndexes(
    settings: TableLayoutShape,
    activeColumnIndexes: readonly number[]
): Pick<TableLayoutShape, 'header' | 'columnAlignments' | 'columnWidths'> {
    return {
        header: activeColumnIndexes.map((columnIndex) => settings.header[columnIndex]),
        columnAlignments: settings.columnAlignments
            ? activeColumnIndexes.map((columnIndex) => settings.columnAlignments![columnIndex])
            : undefined,
        columnWidths: settings.columnWidths
            ? activeColumnIndexes.map((columnIndex) => settings.columnWidths![columnIndex])
            : undefined,
    };
}
